import { ScenarioHarvestTargetType } from 'views/bluviu-nutrition/features/scenarios-v2/types/scenario-cage-data.type';
import { FishGroupSummary, Summary } from '../types/scenario-result.type';

type ApiSummary = Partial<Summary> & {
  averageFeedCostPerKgFeed?: number;
  feedCostOfHogGain?: number;
  daysToReachTargetWeight?: number;
  gwpPerKgGrowth?: number;
  gwpPerKgFeed?: number;
  hoGainGrams?: number;
  totalCost?: number;
  potentialSalesValue?: number;
  potentialProfit?: number;
};

const safeNumber = (value: unknown): number => {
  const numberValue = Number(value);
  return Number.isFinite(numberValue) ? numberValue : 0;
};

const safeDivide = (numerator: number, denominator: number): number => {
  return denominator > 0 && Number.isFinite(numerator) && Number.isFinite(denominator) ? numerator / denominator : 0;
};

const sum = (values: number[]): number => {
  return values.reduce((acc, value) => acc + (Number.isFinite(value) ? value : 0), 0);
};

const maxOrZero = (values: number[]): number => {
  const validValues = values.filter(Number.isFinite);
  return validValues.length > 0 ? Math.max(...validValues) : 0;
};

const normalizeApiSummary = (summary: ApiSummary): Summary => ({
  id: summary.id ?? summary.feedPlan ?? '',
  feedPlanId: summary.feedPlanId ?? '',
  feedPlan: summary.feedPlan ?? '',
  feeds: summary.feeds ?? [],
  feedPlanMaxWeeks: safeNumber(summary.feedPlanMaxWeeks),
  feedPlanMaxWeight: safeNumber(summary.feedPlanMaxWeight),
  finalWeightGrams: safeNumber(summary.finalWeightGrams),
  daysToReach: safeNumber(summary.daysToReach ?? summary.daysToReachTargetWeight),
  weightGainGrams: safeNumber(summary.weightGainGrams),
  feedIntakeGrams: safeNumber(summary.feedIntakeGrams),
  bFcr: safeNumber(summary.bFcr),
  eFcr: safeNumber(summary.eFcr),
  carcassYieldPercent: safeNumber(summary.carcassYieldPercent),
  filletFatPercent: safeNumber(summary.filletFatPercent),
  hogGainGrams: safeNumber(summary.hogGainGrams ?? summary.hoGainGrams),
  globalWarmingPotentialCo2KgPerKgGrowth: safeNumber(summary.globalWarmingPotentialCo2KgPerKgGrowth ?? summary.gwpPerKgGrowth),
  globalWarmingPotentialCo2KgPerKgFeed: safeNumber(summary.globalWarmingPotentialCo2KgPerKgFeed ?? summary.gwpPerKgFeed),
  averageFeedCostPerKgFeed: safeNumber(summary.averageFeedCostPerKgFeed),
  feedCostOfGrowth: safeNumber(summary.feedCostOfGrowth),
  feedCostOfHogGain: safeNumber(summary.feedCostOfHogGain), // note: not sure if this is should be added here but types required it.
  totalCost: safeNumber(summary.totalCost),
  potentialSalesValue: safeNumber(summary.potentialSalesValue),
  potentialProfit: safeNumber(summary.potentialProfit),
  icon: summary.icon,
  appliedAs: summary.appliedAs,
  costBreakdown: {
    feedCostTotal: safeNumber(summary.costBreakdown?.feedCostTotal),
    smoltCostTotal: safeNumber(summary.costBreakdown?.smoltCostTotal),
    harvestCostTotal: safeNumber(summary.costBreakdown?.harvestCostTotal),
    operationsCostTotal: safeNumber(summary.costBreakdown?.operationsCostTotal),
    treatmentCostTotal: safeNumber(summary.costBreakdown?.treatmentCostTotal),
  },
});

const normalizeApiFishGroupSummary = (summary: Partial<FishGroupSummary>): FishGroupSummary => ({
  ...normalizeApiSummary(summary),
  fishGroupId: summary.fishGroupId!,
  mortalityProfileId: safeNumber(summary.mortalityProfileId),
  mortalityProfileName: summary.mortalityProfileName ?? '',
  harvestTarget: safeNumber(summary.harvestTarget),
  fishgroupName: summary.fishgroupName ?? '',
  harvestTargetType: summary.harvestTargetType ?? ScenarioHarvestTargetType.WEEK,
  daysToReachTargetWeight: safeNumber(summary.daysToReachTargetWeight),
  gwpPerKgGrowth: safeNumber(summary.gwpPerKgGrowth),
  gwpPerKgFeed: safeNumber(summary.gwpPerKgFeed),
  totalFeedKg: safeNumber(summary.totalFeedKg),
  totalNetProduction: safeNumber(summary.totalNetProduction),
  totalGrossProduction: safeNumber(summary.totalGrossProduction),
  totalGuttedProduction: safeNumber(summary.totalGuttedProduction),
  totalGwp: safeNumber(summary.totalGwp),
  endpointFishCount: safeNumber(summary.endpointFishCount),
  finalWeightNumerator: safeNumber(summary.finalWeightNumerator),
  weightGainNumerator: safeNumber(summary.weightGainNumerator),
  hogGainNumerator: safeNumber(summary.hogGainNumerator),
  carcassYieldNumerator: safeNumber(summary.carcassYieldNumerator),
  filletFatPercentNumerator: safeNumber(summary.filletFatPercentNumerator),
  totalCumulativeFeedCost: safeNumber(summary.totalCumulativeFeedCost),
  maxSpanDays: safeNumber(summary.maxSpanDays),
});

export const buildSummaries = (siteSummary: Partial<Summary>[] = []): Summary[] => {
  return siteSummary.map(normalizeApiSummary).sort((a, b) => a.feedPlan.localeCompare(b.feedPlan));
};

export const buildFishGroupSummaries = (fishGroupSummaries: Partial<FishGroupSummary>[] = []): FishGroupSummary[] => {
  return fishGroupSummaries.map(normalizeApiFishGroupSummary).sort((a, b) => a.feedPlan.localeCompare(b.feedPlan));
};

export const aggregateFishGroupSummariesToSiteSummaries = (fishGroupSummaries: FishGroupSummary[]): Summary[] => {
  const groupedByFeedPlan = fishGroupSummaries.reduce<Record<string, FishGroupSummary[]>>((acc, summary) => {
    const feedPlan = summary.feedPlan || '';
    acc[feedPlan] = acc[feedPlan] ?? [];
    acc[feedPlan].push(summary);
    return acc;
  }, {});

  return Object.entries(groupedByFeedPlan)
    .map(([feedPlan, summaries]) => {
      const first = summaries[0];
      const totalFeedKg = sum(summaries.map((summary) => summary.totalFeedKg));
      const totalNetProduction = sum(summaries.map((summary) => summary.totalNetProduction));
      const totalGrossProduction = sum(summaries.map((summary) => summary.totalGrossProduction));
      const totalGuttedProduction = sum(summaries.map((summary) => summary.totalGuttedProduction));
      const totalGwp = sum(summaries.map((summary) => summary.totalGwp));
      const endpointFishCount = sum(summaries.map((summary) => summary.endpointFishCount));
      const finalWeightNumerator = sum(summaries.map((summary) => summary.finalWeightNumerator));
      const weightGainNumerator = sum(summaries.map((summary) => summary.weightGainNumerator));
      const hogGainNumerator = sum(summaries.map((summary) => summary.hogGainNumerator));
      const carcassYieldNumerator = sum(summaries.map((summary) => summary.carcassYieldNumerator));
      const filletFatPercentNumerator = sum(summaries.map((summary) => summary.filletFatPercentNumerator));
      const totalCumulativeFeedCost = sum(summaries.map((summary) => summary.totalCumulativeFeedCost));
      const totalCost = sum(summaries.map((summary) => summary.totalCost));
      const potentialSalesValue = sum(summaries.map((summary) => summary.potentialSalesValue));
      const potentialProfit = sum(summaries.map((summary) => summary.potentialProfit ?? 0));
      const feedCostTotal = sum(summaries.map((summary) => summary.costBreakdown?.feedCostTotal ?? 0));
      const smoltCostTotal = sum(summaries.map((summary) => summary.costBreakdown?.smoltCostTotal ?? 0));
      const harvestCostTotal = sum(summaries.map((summary) => summary.costBreakdown?.harvestCostTotal ?? 0));
      const operationsCostTotal = sum(summaries.map((summary) => summary.costBreakdown?.operationsCostTotal ?? 0));
      const treatmentCostTotal = sum(summaries.map((summary) => summary.costBreakdown?.treatmentCostTotal ?? 0));

      return {
        id: feedPlan,
        feedPlan,
        feedPlanId: first.feedPlanId,
        feeds: first?.feeds ?? [],
        feedPlanMaxWeeks: maxOrZero(summaries.map((summary) => summary.feedPlanMaxWeeks)),
        feedPlanMaxWeight: maxOrZero(summaries.map((summary) => summary.feedPlanMaxWeight)),
        finalWeightGrams: safeDivide(finalWeightNumerator, endpointFishCount),
        daysToReach: maxOrZero(summaries.map((summary) => summary.maxSpanDays)),
        weightGainGrams: safeDivide(weightGainNumerator, endpointFishCount),
        feedIntakeGrams: safeDivide(totalFeedKg * 1000, endpointFishCount),
        bFcr: safeDivide(totalFeedKg, totalGrossProduction),
        eFcr: safeDivide(totalFeedKg, totalNetProduction),
        carcassYieldPercent: safeDivide(carcassYieldNumerator, endpointFishCount) * 100,
        filletFatPercent: safeDivide(filletFatPercentNumerator, endpointFishCount),
        hogGainGrams: safeDivide(hogGainNumerator, endpointFishCount),
        globalWarmingPotentialCo2KgPerKgGrowth: safeDivide(totalGwp, totalGuttedProduction),
        globalWarmingPotentialCo2KgPerKgFeed: safeDivide(totalGwp, totalFeedKg),
        averageFeedCostPerKgFeed: safeDivide(totalCumulativeFeedCost, totalFeedKg),
        feedCostOfGrowth: safeDivide(totalCumulativeFeedCost, totalNetProduction),
        feedCostOfHogGain: safeDivide(totalCumulativeFeedCost, totalGuttedProduction),
        totalCost,
        potentialSalesValue,
        potentialProfit,
        icon: first?.icon,
        appliedAs: first?.appliedAs,
        costBreakdown: {
          feedCostTotal,
          smoltCostTotal,
          harvestCostTotal,
          operationsCostTotal,
          treatmentCostTotal,
        },
      };
    })
    .sort((a, b) => a.feedPlan.localeCompare(b.feedPlan));
};
